import Events from './events';
export class RefreshExpiredError {
}
export class HttpError {
    constructor(StatusCode) {
        this.StatusCode = StatusCode;
    }
}
export class NiceError extends HttpError {
    constructor(statusCode, Msg) {
        super(statusCode);
        this.Msg = Msg;
    }
}
export class AspNetUser {
    constructor(Raw) {
        this.Raw = Raw;
    }
    get lockoutEndDate() {
        return new Date(this.Raw.lockoutEnd);
    }
    get isLockedOut() {
        return this.lockoutEndDate > new Date();
    }
}
var TokenType;
(function (TokenType) {
    TokenType[TokenType["Access"] = 0] = "Access";
    TokenType[TokenType["Refresh"] = 1] = "Refresh";
})(TokenType || (TokenType = {}));
async function ThrowIfNotSuccess(rsp) {
    if (rsp.status < 200 || rsp.status >= 300) {
        if (rsp.headers.has("Wsb-Error")) {
            const wsbErr = rsp.headers.get("Wsb-Error");
            throw new NiceError(rsp.status, wsbErr);
        }
        else {
            let msg = null;
            try {
                const cntTyp = rsp.headers.get('Content-Type');
                if (cntTyp != null && cntTyp.toLowerCase().indexOf('text/plain') != -1) {
                    msg = await rsp.text();
                }
            }
            catch (e) { }
            if (msg != null) {
                throw new NiceError(rsp.status, msg);
            }
            else {
                throw new HttpError(rsp.status);
            }
        }
    }
}
function reqTokenBasic(authUrl, usr, pwd, type = TokenType.Access) {
    const b64 = btoa(usr + ':' + pwd);
    return reqTokenWithHeader(authUrl, "Basic", { 'Authorization': 'Basic ' + b64 }, type);
}
function reqTokenBearer(authUrl, token) {
    return reqTokenWithHeader(authUrl, "Token", { 'Authorization': 'Bearer ' + token });
}
async function reqTokenWithHeader(authUrl, loginType, headers, tokenType = null) {
    let url = authUrl + "/Login/" + loginType;
    if (tokenType !== null) {
        const typStr = tokenType == TokenType.Access ? "Access" :
            tokenType == TokenType.Refresh ? "Refresh" : ((n) => n)(tokenType);
        url += "/" + typStr;
    }
    const resp = await fetch(url, {
        method: 'GET',
        headers: new Headers(headers)
    });
    await ThrowIfNotSuccess(resp);
    const jsn = await resp.json();
    return jsn.token;
}
const _ratKey = 'RawAccToken';
const _rrtKey = 'RawRefToken';
const _rttKey = (usr) => `RawTrustToken_${usr.toLowerCase()}`;
function getRawAccToken() { return localStorage.getItem(_ratKey); }
function setRawAccToken(tok) { localStorage.setItem(_ratKey, tok); }
function clearRawAccToken() { localStorage.removeItem(_ratKey); }
function getRawRefToken() { return localStorage.getItem(_rrtKey); }
function setRawRefToken(tok) { localStorage.setItem(_rrtKey, tok); }
function clearRawRefToken() { localStorage.removeItem(_rrtKey); }
function getRawTrustToken(usr) { return localStorage.getItem(_rttKey(usr)); }
function setRawTrustToken(tok, usr) { localStorage.setItem(_rttKey(usr), tok); }
function clearRawTrustToken(usr) { localStorage.removeItem(_rttKey(usr)); }
function clearLoginTokens() {
    clearRawAccToken();
    clearRawRefToken();
}
function parseToken(rawToken) {
    const b64u = rawToken.split('.')[1];
    const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
    const res = JSON.parse(window.atob(b64));
    return res;
}
function isTokenExpired(token) {
    const res = (token.exp * 1000) - Date.now() <= 0;
    return res;
}
async function updateAccToken(authUrl, onlyIfNeeded = false) {
    const rawRefT = getRawRefToken();
    if (rawRefT != null) {
        const refT = parseToken(rawRefT);
        const rawAccT = getRawAccToken();
        if (onlyIfNeeded && rawAccT != null && !isTokenExpired(parseToken(rawAccT))) {
            return rawAccT;
        }
        else {
            if (isTokenExpired(refT)) {
                throw new RefreshExpiredError();
            }
            else {
                const gotAccT = await reqTokenBearer(authUrl, rawRefT);
                setRawAccToken(gotAccT);
                return gotAccT;
            }
        }
    }
    throw "Cannot 'UpdateAccToken', refresh token is not set.";
}
class Impersonator {
    constructor(auth) {
        this.auth = auth;
        this.OrgTokKey = 'Impersonator_OrgRefTok';
    }
    async impersonate(username) {
        const req = new Request(`${this.auth.AuthUrl}/Login/Impersonate/${username}`);
        await this.auth.configureRequest(req);
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
        const jsn = await resp.json();
        this.orgToken = getRawRefToken();
        clearLoginTokens();
        setRawRefToken(jsn.token);
    }
    logout() {
        if (this.orgToken != null) {
            clearLoginTokens();
            setRawRefToken(this.orgToken);
            this.orgToken = null;
        }
    }
    get isImpersonating() { return this.orgToken != null; }
    get orgToken() { return localStorage.getItem(this.OrgTokKey); }
    set orgToken(val) { val != null ? localStorage.setItem(this.OrgTokKey, val) : localStorage.removeItem(this.OrgTokKey); }
}
export class Auth {
    constructor(AuthUrl) {
        this.AuthUrl = AuthUrl;
        this.updateAccTokenPromise = null;
        this.impersonator = new Impersonator(this);
    }
    Logout() {
        if (this.impersonator.isImpersonating) {
            this.impersonator.logout();
        }
        else {
            clearLoginTokens();
            Events.RaiseDeauthorized();
        }
    }
    async GetRawToken() {
        if (this.updateAccTokenPromise == null) {
            this.updateAccTokenPromise = updateAccToken(this.AuthUrl, true);
        }
        try {
            return await this.updateAccTokenPromise;
        }
        catch (e) {
            this.Logout();
            Events.RaiseDeauthorized(e);
            throw e;
        }
        finally {
            this.updateAccTokenPromise = null;
        }
    }
    async GetToken() {
        const rawToken = await this.GetRawToken();
        return parseToken(rawToken);
    }
    IsLoggedIn() {
        const raw = getRawRefToken();
        if (raw != null) {
            const tok = parseToken(raw);
            return !isTokenExpired(tok) && tok.mfa != "0";
        }
        return false;
    }
    async Login(usr, pwd) {
        clearLoginTokens(); // Clear any old tokens
        let rawRefTok = await reqTokenBasic(this.AuthUrl, usr, pwd, TokenType.Refresh);
        const refTok = parseToken(rawRefTok);
        if (refTok.mfa == "0") {
            const rawTrustTok = getRawTrustToken(usr);
            if (rawTrustTok != null) {
                const trustTok = parseToken(rawTrustTok);
                if (!isTokenExpired(trustTok)) {
                    try {
                        rawRefTok = await this.AuthorizeToken(rawTrustTok, rawRefTok);
                    }
                    catch (e) {
                        clearRawTrustToken(usr);
                    }
                }
                else {
                    clearRawTrustToken(usr);
                }
            }
        }
        setRawRefToken(rawRefTok);
    }
    async ChangePwd(username, token, newPwd) {
        const rsp = await fetch(`${this.AuthUrl}/Login/ChangePassword`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json'
            }),
            body: JSON.stringify({
                Username: username,
                Token: token,
                NewPassword: newPwd
            })
        });
        await ThrowIfNotSuccess(rsp);
    }
    async configureRequest(request) {
        const tkn = await this.GetRawToken();
        return this.configureRequestWithBearer(request, tkn);
    }
    configureRequestWithBearer(request, token) {
        request.headers.append('Authorization', `Bearer ${token}`);
        return request;
    }
    async getUsers() {
        const req = new Request(`${this.AuthUrl}/User`);
        req.headers.append('Accept', 'application/json');
        await this.configureRequest(req);
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
        const res = await resp.json();
        return res.map(r => new AspNetUser(r));
    }
    async lockoutUser(userId) {
        const req = await this.configureRequest(new Request(`${this.AuthUrl}/User/${userId}/Lockout`, { method: 'POST' }));
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
    }
    async disableLockoutUser(userId) {
        const req = await this.configureRequest(new Request(`${this.AuthUrl}/User/${userId}/DisableLockout`, { method: 'POST' }));
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
    }
    async resetPassword(userId) {
        const resp = await fetch(new Request(`${this.AuthUrl}/Login/ResetPassword/${userId}`, { method: 'POST' }));
        await ThrowIfNotSuccess(resp);
    }
    impersonate(username) {
        return this.impersonator.impersonate(username);
    }
    async RequestSendMfaToken() {
        const req = await this.configureRequest(new Request(`${this.AuthUrl}/MFA/SendToken`, { method: 'POST' }));
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
    }
    async MfaLogin(oneTimePassword, trustThisDevice) {
        const rawRefTkn = getRawRefToken();
        if (rawRefTkn != null) {
            const req = await this.configureRequest(new Request(`${this.AuthUrl}/MFA/Verify/${oneTimePassword}`));
            const resp = await fetch(req);
            await ThrowIfNotSuccess(resp);
            const rawTrustTkn = (await resp.json()).token;
            if (trustThisDevice) {
                setRawTrustToken(rawTrustTkn, parseToken(rawRefTkn).sub);
            }
            const newRawRefTkn = await this.AuthorizeToken(rawTrustTkn, rawRefTkn);
            setRawRefToken(newRawRefTkn);
            clearRawAccToken();
        }
        else {
            throw 'Unsupported: call "MfaLogin" when raw refresh token is not set.';
        }
    }
    async GetMfaEnabled() {
        const req = await this.configureRequest(new Request(`${this.AuthUrl}/MFA/Enabled`));
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
        const jsn = await resp.json();
        return jsn;
    }
    async SetMfaEnabled(value) {
        const req = await this.configureRequest(new Request(`${this.AuthUrl}/MFA/Enabled`, {
            method: 'POST',
            body: JSON.stringify(value),
            headers: new Headers({ 'Content-Type': 'application/json' })
        }));
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
    }
    async AuthorizeToken(rawTrustToken, rawToken) {
        const body = JSON.stringify({ Token: rawToken });
        let req = new Request(`${this.AuthUrl}/MFA/Authorize`, { method: 'POST', body: body, headers: new Headers({ 'Content-Type': 'application/json' }) });
        req = this.configureRequestWithBearer(req, rawTrustToken);
        const resp = await fetch(req);
        await ThrowIfNotSuccess(resp);
        const rspObj = await resp.json();
        return (rspObj.token);
    }
}
//# sourceMappingURL=auth.js.map